<template>
  <div class="attendance-status-report-print">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Attendance Status Report</h4>
        <b-badge
          v-show="!loading && !error"
          variant="light-primary"
          class="ml-2"
        >
          {{ rows.length }} students
        </b-badge>
      </div>
      <div class="action-buttons">
        <button type="button" class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button type="button" class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">{{ loadingText }}</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <!-- Report -->
    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                — {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1 flex-wrap">
              <span class="report-name-text">Attendance Status Report</span>
              <span class="date-range-text">
                <template v-if="periodLabel">
                  <strong>Period:</strong> {{ periodLabel }}
                  <span class="mx-2 text-muted">|</span>
                </template>
                <template v-if="campusLabel">
                  <strong>Campus:</strong> {{ campusLabel }}
                  <span class="mx-2 text-muted">|</span>
                </template>
                <strong>Records:</strong> {{ rows.length }}
                <span class="mx-2 text-muted">|</span>
                <strong>Generated on {{ generatedAt }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 4%">#</th>
              <th class="text-left" style="width: 22%">STUDENT</th>
              <th class="text-center" style="width: 14%">CLASS / SECTION</th>
              <th
                v-for="mid in distinctPrintMonths"
                :key="'h-' + mid"
                class="text-center month-brk-col"
              >
                {{ monthShortLabel(mid) }}
              </th>
              <th class="text-center" style="width: 9%">TOTAL</th>
              <th class="text-center" style="width: 9%">PRESENT</th>
              <th class="text-center" style="width: 9%">ABSENT</th>
              <th class="text-center" style="width: 9%">%</th>
              <!-- <th class="text-center" style="width: 12%">SUGGESTED STATUS</th>  -->
              <th class="text-center" style="width: 14%">STATUS</th>
              <th class="text-center" style="width: 12%">Comment</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in rows"
              :key="row.summaryId || row.sID || index"
            >
              <td class="text-center font-weight-bold">{{ index + 1 }}</td>
              <td class="text-left student-cell">
                <div class="student-name-bold">
                  {{ (row.name || "—").toUpperCase() }}
                </div>
                <div v-if="isTcf" class="student-gr-sub">
                  FB Reg. No.
                  {{
                    row.studentDetails
                      ? row.studentDetails.federalRegNo || "—"
                      : "—"
                  }}
                </div>
                <div v-else class="student-gr-sub">
                  GR: {{ row.grNo || "—" }}
                </div>
              </td>
              <td class="text-center">
                {{ formatClassSection(row) }}
              </td>
              <td
                v-for="mid in distinctPrintMonths"
                :key="'c-' + mid + '-' + (row.sID != null ? row.sID : index)"
                class="text-center font-weight-bold month-brk-cell"
              >
                <template v-if="getMonthlyBreakdownForMonth(row, mid)">
                  <div class="month-brk-pt">
                    {{ getMonthlyBreakdownForMonth(row, mid).presentDays }}/{{
                      getMonthlyBreakdownForMonth(row, mid).totalDays
                    }}
                  </div>
                  <b-badge
                    variant="light-primary"
                    class="pct-badge font-weight-bold"
                  >
                    {{ getMonthlyBreakdownForMonth(row, mid).percentage }}%
                  </b-badge>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center font-weight-bold">
                {{ row.totalDays != null ? row.totalDays : "—" }}
              </td>
              <td class="text-center font-weight-bold cell-present">
                {{ row.presentDays != null ? row.presentDays : "—" }}
              </td>
              <td class="text-center font-weight-bold cell-absent">
                {{ row.absentDays != null ? row.absentDays : "—" }}
              </td>
              <td class="text-center pct-cell">
                <b-badge
                  v-if="row.percentage != null"
                  :variant="percentageVariant(row.percentage)"
                  class="pct-badge font-weight-bold"
                >
                  {{ Number(row.percentage) }}%
                </b-badge>
                <span v-else class="text-muted">—</span>
              </td>
              <!-- <td class="text-center suggested-status-cell">
                <b-badge
                  v-if="row.suggestedStudentStatus"
                  :variant="statusVariant(row.suggestedStudentStatus)"
                  class="status-badge font-weight-bold"
                >
                  {{ row.suggestedStudentStatus.toUpperCase() }}
                </b-badge>
                <span v-else class="text-muted">—</span>
              </td> -->
              <td class="text-center status-cell">
                <b-badge
                  v-if="row.studentDetails"
                  :variant="statusVariant(row.studentDetails.studentStatus)"
                  class="status-badge font-weight-bold"
                >
                  {{ row.studentDetails.studentStatus.toUpperCase() }}
                </b-badge>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center font-weight-bold">
                {{ row.comment != null ? row.comment : " " }}
              </td>
            </tr>
            <tr v-if="rows.length === 0">
              <td :colspan="emptyTableColspan" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p class="mb-0">No records for the selected period.</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

const CALENDAR_MONTHS = [
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
];

export default {
  name: "MonthlyAttendanceStatusReportPrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: false,
      loadingText: "Loading attendance status…",
      error: null,
      rows: [],
      db: "",
      cID: null,
      periodLabel: "",
      campusLabel: "",
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
    };
  },
  computed: {
    isTcf() {
      return ["myskooltcfdemo", "myskooltcf"].includes(
        this.$store.state.report.replace(/_/g, "")
      );
    },
    generatedAt() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    printedBy() {
      return this.$store.state.userData?.name || "—";
    },
    /** Distinct month numbers from monthlyBreakdown across loaded rows */
    distinctPrintMonths() {
      const set = new Set();
      for (const row of this.rows || []) {
        const arr = row.monthlyBreakdown;
        if (!Array.isArray(arr)) continue;
        for (const b of arr) {
          if (b != null && b.month != null) set.add(Number(b.month));
        }
      }
      return [...set].sort((a, b) => a - b);
    },
    emptyTableColspan() {
      return 9 + this.distinctPrintMonths.length;
    },
  },
  created() {
    this.readQuery();
    this.bootstrap();
  },
  methods: {
    readQuery() {
      const q = this.$route.query;
      this.db = q.db || this.$store.state.userData.db;
      this.cID =
        q.campusID != null && q.campusID !== ""
          ? q.campusID
          : this.$store.state.userData.cId;
      this.periodLabel = (q.periodLabel && String(q.periodLabel)) || "";
      this.campusLabel = (q.campusLabel && String(q.campusLabel)) || "";
    },
    async bootstrap() {
      await this.loadCampusInfo();
      await this.loadReportData();
    },
    async loadCampusInfo() {
      if (!this.db) return;
      try {
        const url = `${
          this.$store.state.domain
        }campuses?db=${encodeURIComponent(this.db)}&cId=${encodeURIComponent(
          this.$store.state.userData.cId
        )}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });
        const list = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.data) || [];
        const match = list.find((c) => String(c.id) === String(this.cID));
        if (match) {
          this.campusName =
            match.organizationName || match.name || match.branch || "MY SKOOL";
          this.campusBranch = match.branch || "";
          this.schoolLogo = match.logo || match.logoUrl || null;
        } else {
          this.applyCampusFallback();
        }
      } catch (e) {
        console.warn("Campus info fetch failed:", e);
        this.applyCampusFallback();
      }
    },
    applyCampusFallback() {
      const branches = this.$store.state.branches || [];
      const match = branches.find((b) => String(b.id) === String(this.cID));
      if (match) {
        this.campusName = match.organizationName || match.branch || "MY SKOOL";
        this.campusBranch = match.branch || "";
        this.schoolLogo = match.logo || match.logoUrl || null;
      } else if (this.campusLabel) {
        this.campusName = this.campusLabel;
        this.campusBranch = "";
      } else {
        this.campusName = this.$store.state.userData?.campusName || "MY SKOOL";
        this.campusBranch = "";
      }
    },
    monthShortLabel(monthId) {
      const m = CALENDAR_MONTHS.find((x) => x.id === Number(monthId));
      return m ? m.name.slice(0, 3) : String(monthId);
    },
    getMonthlyBreakdownForMonth(row, monthNum) {
      const arr = row && row.monthlyBreakdown;
      if (!Array.isArray(arr)) return null;
      const n = Number(monthNum);
      return arr.find((b) => b != null && Number(b.month) === n) || null;
    },
    formatClassSection(row) {
      const cn = row.className || "";
      const sn = row.sectionName;
      if (sn) return `${cn} – ${sn}`;
      return cn || "—";
    },
    percentageVariant(pct) {
      if (pct == null) return "light-secondary";
      const n = Number(pct);
      if (Number.isNaN(n)) return "light-secondary";
      if (n < 55) return "light-danger";
      if (n <= 74) return "light-warning";
      return "light-success";
    },
    statusVariant(status) {
      if (!status) return "secondary";
      const s = String(status).toLowerCase();
      if (s === "active") return "light-success";
      if (s === "in-active" || s === "inactive") return "light-danger";
      if (s === "on-probation") return "light-warning";
      return "secondary";
    },
    async loadReportData() {
      const q = this.$route.query;
      const db = q.db;
      const campusID =
        q.campusID != null && q.campusID !== "" ? q.campusID : null;
      const month = q.month;
      const year = q.year;

      if (!db || month == null || month === "" || year == null || year === "") {
        this.error =
          "Missing report parameters. Open this page from Monthly Attendance.";
        return;
      }

      this.loading = true;
      this.error = null;

      const cid =
        campusID != null && campusID !== ""
          ? campusID
          : this.$store.state.userData?.cId;

      const monthsParam = String(month)
        .split(",")
        .map((s) => parseInt(String(s).trim(), 10))
        .filter((n) => !Number.isNaN(n) && n >= 1 && n <= 12);
      if (!monthsParam.length) {
        this.error = "Missing or invalid months for report.";
        this.loading = false;
        return;
      }

      let url = `${this.$store.state.domain}AttendanceSummary/LoadStatusReportMultiMonth?`;
      url += `db=${encodeURIComponent(db)}`;
      url += `&campusID=${encodeURIComponent(cid)}`;
      url += `&months=${encodeURIComponent(monthsParam.join(","))}`;
      url += `&year=${encodeURIComponent(year)}`;

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        const body = response.data;
        let list = [];
        if (Array.isArray(body)) list = body;
        else if (body && Array.isArray(body.data)) list = body.data;
        this.rows = list;
      } catch (err) {
        console.error("Attendance status report print error:", err);
        this.error =
          "Failed to load report data. Please check your connection and try again.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.attendance-status-report-print {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: 130px;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.05rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
      flex-shrink: 0;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.table-container {
  margin-top: 0.25rem;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;
    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.85rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  .student-cell .student-name-bold {
    font-weight: 700;
    font-size: 0.9rem;
    color: #000;
  }

  .student-cell .student-gr-sub {
    font-size: 0.8rem;
    color: #636e72;
    margin-top: 4px;
  }

  .pct-cell,
  .status-cell {
    vertical-align: middle;
  }

  .pct-cell .pct-badge,
  .status-cell .status-badge {
    font-size: 0.82rem;
    padding: 0.4em 0.65em;
    vertical-align: middle;
  }

  .cell-present {
    color: #16a34a;
  }

  .cell-absent {
    color: #dc2626;
  }

  .no-data p {
    color: #636e72;
    font-weight: 500;
  }
}

.page-footer {
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  color: #adb5bd;
}

.no-break {
  break-inside: avoid;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .attendance-status-report-print {
    background: white !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    display: block !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    min-height: 0 !important;
    height: auto !important;
    page-break-after: auto !important;
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;
    page-break-after: avoid !important;
    break-after: avoid !important;

    .school-logo-img {
      height: 100px !important;
      max-width: 100px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.6rem !important;
    }

    .report-info-line {
      font-size: 0.85rem !important;
      flex-wrap: wrap !important;

      .report-name-text {
        border-right: 2px solid #000 !important;
      }
    }
  }

  .table-container {
    margin-top: 0 !important;
    page-break-inside: auto !important;
  }

  .ledger-style-table {
    font-size: 8pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    tbody tr:nth-child(even) {
      background-color: #fafafa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .badge {
      border: 1px solid rgba(0, 0, 0, 0.12) !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .cell-present,
    .cell-absent {
      color: #000 !important;
    }

    tr {
      break-inside: avoid;
    }

    tbody tr:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }

    thead {
      display: table-header-group;
    }
  }

  .page-footer {
    color: #666 !important;
    margin-top: 1rem !important;
    padding-top: 1rem !important;
    page-break-before: avoid !important;
    break-inside: avoid !important;
  }
}
</style>

<style lang="scss">
/* Unscoped: print layout uses full window; min-height on html/body/#app often causes an extra blank page. */
@media print {
  html,
  body {
    height: auto !important;
    min-height: 0 !important;
    background: #fff !important;
  }

  #app {
    min-height: 0 !important;
    height: auto !important;
  }
}
</style>
